import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, IconButton, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';

import { useAuthStore } from '../store/authStore';
import { tokens } from '../theme';

/**
 * Sticky reminder strip shown on every non-home page when the signed-in user
 * has not yet verified ownership of a Sleeper account.
 *
 * Until verification, the user can't rate leaguemates, can't rate
 * commissioners, and can't reply to reviews — i.e. most of the product is
 * read-only. The banner gives them a one-click path to the dashboard with
 * the link/verify dialog auto-opened (`?verify=1`).
 *
 * Dismissal: persists for 8 hours per browser session via sessionStorage,
 * so an active browse isn't peppered with the banner but a fresh visit
 * later in the day reminds them again.
 */
const DISMISS_KEY = 'verifyBannerDismissedAt';
const REAPPEAR_AFTER_MS = 8 * 60 * 60 * 1000;

export default function VerifyBanner() {
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const stamp = sessionStorage.getItem(DISMISS_KEY);
    if (!stamp) return;
    const ts = parseInt(stamp, 10);
    if (!Number.isNaN(ts) && Date.now() - ts < REAPPEAR_AFTER_MS) {
      setDismissed(true);
    }
  }, []);

  // Suppress on the homepage (the hero already tells the story for visitors)
  // and on the dashboard itself (the onboarding checklist takes over there).
  const onHome = location.pathname === '/';
  const onDashboard = location.pathname.startsWith('/dashboard');

  if (onHome || onDashboard) return null;
  if (!isAuthenticated || !user || user.isVerified) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  return (
    <Box
      role="status"
      sx={{
        backgroundColor: 'rgba(232, 114, 42, 0.10)',
        borderBottom: `1px solid ${tokens.orange}`,
        px: { xs: 2, md: 5 },
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, md: 2 },
      }}
    >
      <VerifiedUserIcon
        sx={{ color: tokens.orange, fontSize: 22, flexShrink: 0 }}
      />
      <Typography
        variant="body2"
        sx={{
          color: tokens.text,
          fontFamily: '"DM Sans", sans-serif',
          flexGrow: 1,
          lineHeight: 1.4,
        }}
      >
        <Box component="span" sx={{ fontWeight: 700, color: tokens.orange2 }}>
          Verify your Sleeper account
        </Box>{' '}
        <Box
          component="span"
          sx={{ color: tokens.text2, display: { xs: 'none', sm: 'inline' } }}
        >
          to rate leaguemates, reply to reviews, and unlock the rest of
          DynastyRep.
        </Box>
      </Typography>
      <Button
        component={RouterLink}
        to="/dashboard?verify=1"
        size="small"
        variant="contained"
        sx={{
          backgroundColor: tokens.orange,
          color: '#fff',
          '&:hover': { backgroundColor: tokens.orange2 },
          flexShrink: 0,
          fontSize: 12,
          py: 0.75,
          px: 2,
        }}
      >
        Verify Now
      </Button>
      <IconButton
        size="small"
        onClick={handleDismiss}
        aria-label="Dismiss verification reminder"
        sx={{ color: tokens.text2, flexShrink: 0 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
